import { useEffect, useState } from 'react';

import { css } from '@emotion/react';
import { Box, Icons, Image, Text, TextButton } from '@opendoor/bricks-next';

interface Review {
  rating: number;
  excerpt: string;
  reviewer: {
    name: string;
    city: string;
    productType: string;
  };
}

const STUBBED_REVIEWS: Review[] = [
  {
    rating: 5,
    excerpt: 'Great experience from beginning...',
    reviewer: {
      name: 'Verified Customer',
      city: 'San Antonio',
      productType: 'sell',
    },
  },
  {
    rating: 5,
    excerpt: 'The process is very efficient and hassle-free...',
    reviewer: {
      name: 'Verified Customer',
      city: 'Oklahoma City Ok',
      productType: 'sell',
    },
  },
  {
    rating: 5,
    excerpt: 'My experience selling to Opendoor was an absolute pleasure.',
    reviewer: {
      name: 'Verified Customer',
      city: 'Phoenix',
      productType: 'sell',
    },
  },
];

export const SocialProofBox = ({ autoplayInterval = 4000 }: { autoplayInterval?: number }) => {
  const [reviews] = useState<Review[]>(STUBBED_REVIEWS);
  const [currentReviewIndex, setCurrentReviewIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentReviewIndex((prevIndex) => (prevIndex === reviews.length - 1 ? 0 : prevIndex + 1));
    }, autoplayInterval);

    return () => clearInterval(interval);
  }, [autoplayInterval]);

  return <CarouselFormat reviews={reviews} currentIndex={currentReviewIndex} />;
};

const CarouselFormat = ({ reviews, currentIndex }: { reviews: Review[]; currentIndex: number }) => {
  if (reviews.length === 0) return null;

  const currentReview = reviews[currentIndex];

  return (
    <Box
      flexDirection="row"
      justifyContent="space-between"
      gap="$5x"
      borderRadius="$12x"
      width="$652x"
      padding="$12x"
      style={{
        background: 'rgba(242, 241, 240, 0.64)',
        backdropFilter: 'blur(20px)',
      }}
      $smallerThanLG={{
        width: '$500x',
        maxHeight: '$105x',
        padding: '$8x',
      }}
      $smallerThanMD={{
        width: '100%',
        minHeight: '$129x',
        borderRadius: '$borderAllowance',
        flexDirection: 'column',
        gap: '$2x',
      }}
    >
      <Box
        flexDirection="column"
        css={css`
          transition: all 2s ease-in-out;
          animation: fadeIn 1s ease-in-out;

          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
        `}
        key={currentIndex}
      >
        <Box flexDirection="row" gap="$1x">
          {new Array(currentReview.rating).fill('star').map((_, index) => (
            <Image
              key={index}
              alt="Rating star"
              source={{
                height: 16,
                uri: 'https://imgdrop.imgix.net/2024-11-1732263853253-76957.svg',
                width: 16,
              }}
            />
          ))}
        </Box>
        <Box flexDirection="row" alignItems="center" gap="$2x">
          <Text
            tag="span"
            typography="$bodySmall"
            color="$contentSecondary"
            $smallerThanLG={{
              typography: '$bodyXsmall',
            }}
          >
            Seller in {currentReview.reviewer.city}
          </Text>
          <Text
            tag="p"
            typography="$labelSmall"
            color="$contentAccentBrand"
            $smallerThanLG={{
              typography: '$labelXsmall',
            }}
          >
            Verified Review
          </Text>
          <Icons.Checkmark size={20} color="$contentAccentBrand" />
        </Box>
        <Text
          tag="span"
          typography="$bodyMedium"
          color="$contentPrimary"
          $smallerThanLG={{
            typography: '$bodySmall',
          }}
        >
          {currentReview.excerpt}
        </Text>
      </Box>

      <Box justifyContent="flex-end">
        <TextButton
          analyticsName="social-proof-carousel-see-reviews"
          aria-label="See reviews"
          onPress={() => window.location.assign('https://opendoor.com/reviews')}
          variant="primary"
          size="small"
          label="See reviews"
          fontWeight="regular"
          $smallerThanLG={{
            size: 'xsmall',
          }}
        />
      </Box>
    </Box>
  );
};

export default SocialProofBox;
